import * as cliJsStepPlayback from './cliJsStepPlayback';
import * as testimServicesApi from '../commons/testimServicesApi';
import * as nodePackageStepPlayback from './nodePackageStepPlayback';
import { getLogger } from '../commons/logger';
import type { RunnerOptions } from '../runOptions';
import type { WebDriver } from '../player/webdriver';
import type { LauncherDriverType } from '../player/chromeLauncherTestPlayer';
import type { SerializableRemoteObject } from 'clickim/src/background/stepActions/remoteStepActionMixin';

const logger = getLogger('step-playback');

type RemoteStepPlayback = typeof cliJsStepPlayback | typeof nodePackageStepPlayback;
const playbacks: Partial<{ [stepType in SerializableRemoteObject['type']]: RemoteStepPlayback }> = {
    'cli-validation-code-step': cliJsStepPlayback,
    'cli-wait-for-code-step': cliJsStepPlayback,
    'cli-action-code-step': cliJsStepPlayback,
    'cli-api-code-step': cliJsStepPlayback,
    'cli-condition-step': cliJsStepPlayback,
    'cli-download-code-step': cliJsStepPlayback,
    'node-package': nodePackageStepPlayback,
};

async function saveRemoteStep(projectId: string, resultId: string, stepId: string, remoteStepData: object) {
    try {
        return await testimServicesApi.saveRemoteStep(projectId, resultId, stepId, remoteStepData);
    } catch (e) {
        logger.error('failed to report remote step state', { projectId, resultId, stepId });
        return undefined;
    }
}

async function onCompleted(projectId: string, resultId: string, stepId: string, runResult: object | undefined, sessionId: string | undefined) {
    logger.info('finished to run remote step', { stepId, sessionId });
    const remoteStepData = {
        status: 'completed',
        success: true,
        failureReason: null,
        data: runResult,
    };

    return await saveRemoteStep(projectId, resultId, stepId, remoteStepData);
}

async function onFailed(projectId: string, resultId: string, stepId: string, failureReason: string, sessionId: string | undefined) {
    logger.info('failed to run remote step', { stepId, sessionId, failureReason });
    const remoteStepData = {
        status: 'completed',
        success: false,
        failureReason,
    };

    return await saveRemoteStep(projectId, resultId, stepId, remoteStepData);
}

export async function executeStep(options: RunnerOptions, driver: WebDriver | LauncherDriverType, step: SerializableRemoteObject | undefined, resultId: string) {
    if (!step?.type || step.status === 'completed') {
        return undefined;
    }

    const { project: projectId, projectData } = options;
    const stepType = step.type;
    const sessionId = driver.getSessionId();
    const stepId = step.id;
    logger.info('start play remote step', { stepType, stepId, sessionId });

    const playback = playbacks[stepType];
    if (!playback) {
        return await onFailed(projectId, resultId, stepId, `Failed to find step type ${stepType}`, sessionId);
    }

    try {
        const runResult = await playback.run(driver, step, projectData!);
        return await onCompleted(projectId, resultId, stepId, runResult, sessionId);
    } catch (err) {
        return await onFailed(projectId, resultId, stepId, err.message, sessionId);
    }
}
